import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputNumber, Switch, Form, Typography, Alert, Select, Row, Col } from 'antd';

const { Title, Text } = Typography;

export type IntervalStepProps = {
  sendingInterval: number
  isActive: boolean
  channel: any
  onIntervalChange: (interval: number) => void
  onActiveChange: (isActive: boolean) => void
}

const IntervalStep: React.FC<IntervalStepProps> = ({
  sendingInterval,
  isActive,
  channel,
  onIntervalChange,
  onActiveChange
}) => {
  const { t } = useTranslation();
  const minimalInterval = channel?.minimalSendingInterval || 0
  
  const toUnit = (minutes: number) => {
    if (minutes % 1440 === 0) return { unit: 'days', value: minutes / 1440 } as const
    if (minutes % 60 === 0) return { unit: 'hours', value: minutes / 60 } as const
    return { unit: 'minutes', value: minutes } as const
  }
  const initialMinutes = minimalInterval && minimalInterval > 0 ? minimalInterval : (sendingInterval || 0)
  const initial = toUnit(initialMinutes || 0)
  const [unit, setUnit] = useState<'minutes'|'hours'|'days'>(initial.unit)
  const [value, setValue] = useState<number | null>(initial.value || null)
  const [intervalError, setIntervalError] = useState<string>('')

  useEffect(() => {
    const minutes = minimalInterval && minimalInterval > 0 ? minimalInterval : (sendingInterval || 0)
    if (!minutes) return
    const converted = toUnit(minutes)
    setUnit(converted.unit)
    setValue(converted.value)
  }, [minimalInterval, sendingInterval])

  const multiplier = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1

  const validate = (val: number | null, u: typeof unit): boolean => {
    if (!val || val <= 0) {
      setIntervalError(t('schedule-interval-invalid-number', { ns: 'labels' }))
      return false
    }
    const m = u === 'days' ? 1440 : u === 'hours' ? 60 : 1
    const minutes = val * m
    if (minutes < minimalInterval) {
      setIntervalError(t('schedule-interval-too-small', { minInterval: minimalInterval, ns: 'labels' }))
      return false
    }
    setIntervalError('')
    return true
  }

  const emitIfValid = (val: number | null, u: typeof unit) => {
    if (validate(val, u) && val) {
      const m = u === 'days' ? 1440 : u === 'hours' ? 60 : 1
      onIntervalChange(val * m)
    }
  }

  const onValueChange = (val: number | null) => {
    setValue(val)
    emitIfValid(val, unit)
  }

  const onUnitChange = (u: 'minutes'|'hours'|'days') => {
    setUnit(u)
    emitIfValid(value, u)
  }

  const getIntervalDisplayText = (): string => {
    if (!value) return ''
    const minutes = value * multiplier
    if (minutes % 1440 === 0) {
      const days = minutes / 1440
      return t('schedule-interval-display-days', { count: days, ns: 'labels' })
    }
    if (minutes === 60) return t('schedule-interval-display-hour', { ns: 'labels' })
    if (minutes % 60 === 0 && minutes > 60) return t('schedule-interval-display-hours', { count: minutes / 60, ns: 'labels' })
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      const hoursText = t('schedule-interval-display-hours', { count: hours, ns: 'labels' })
      const minutesText = t('schedule-interval-display', { count: mins, ns: 'labels' })
      return `${hoursText} ${minutesText}`
    }
    return t('schedule-interval-display', { count: minutes, ns: 'labels' })
  }

  return (
    <div>
      <Title level={4}>{t('schedule-step-interval', { ns: 'labels' })}</Title>
      <Text type="secondary">
        {t('schedule-step-interval-description', { ns: 'labels' })}
      </Text>
      
      <Form layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item
          label={t('schedule-interval-pattern-label', { ns: 'labels' })}
          validateStatus={intervalError ? 'error' : ''}
          help={intervalError}
          required
        >
          <Row gutter={8} align="middle">
            <Col>
              <Text>{t('schedule-interval-every', { ns: 'labels' })}</Text>
            </Col>
            <Col>
              <InputNumber min={1} value={value ?? undefined} onChange={onValueChange} />
            </Col>
            <Col>
              <Select value={unit} style={{ width: 140 }} onChange={onUnitChange}
                options={[
                  { value: 'minutes', label: t('schedule-interval-unit-minutes', { count: value || 1, ns: 'labels' }) },
                  { value: 'hours', label: t('schedule-interval-unit-hours', { count: value || 1, ns: 'labels' }) },
                  { value: 'days', label: t('schedule-interval-unit-days', { count: value || 1, ns: 'labels' }) }
                ]}
              />
            </Col>
          </Row>
          {getIntervalDisplayText() && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{t('schedule-interval-summary', { text: getIntervalDisplayText(), ns: 'labels' })}</Text>
            </div>
          )}
        </Form.Item>

        {minimalInterval > 0 && (
          <Alert
            message={t('schedule-interval-minimum-info', { minInterval: minimalInterval, ns: 'labels' })}
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item label={t('schedule-active-label', { ns: 'labels' })}>
          <Switch
            checked={isActive}
            onChange={onActiveChange}
            checkedChildren={t('schedule-active-on', { ns: 'labels' })}
            unCheckedChildren={t('schedule-active-off', { ns: 'labels' })}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              {t('schedule-active-description', { ns: 'labels' })}
            </Text>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default IntervalStep;
